// キャラの上の操作（指・マウス）（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { SIZE } from './config.js';
import { catByKey } from './store.js';
import { $ } from './util.js';
import { canMove, updateTools } from './tools.js';
import { inBox, pickAt } from './partbox.js';
import { VIEW_MAX, beginGesture, endGesture, resetView, setViewTo } from './view.js';
import { setOffset } from './core/ops.js';

type Point = { x: number; y: number };
// ---- キャラの上の操作（指・マウス） ----
//   移動中: 1本指（マウス）のドラッグで、選んだパーツを動かす（画面の点 → 500 四方の座標。保存データと同じく整数。寄せていても SVG の今の幅で割る）
//   寄せているとき: 1本指（マウス）のドラッグで、見る場所を動かす
//   2本指: ピンチで拡大・縮小と見る場所の移動（移動中でも）。PC はホイール（トラックパッドのピンチ = ctrl+ホイール）で、指した所を中心に
//   ダブルタップ（ダブルクリック）: 拡大しているときは全身に
export function bindStage() {
  // start: 動かし始めの値（mode ごとに入れるものが違う。part: x・y・tx・ty・k、pan: x・y・vx・vy・w、pinch: d・s・ux・uy）
  var stage = $('stage'), pts = new Map<number, Point>(), mode: 'part' | 'pan' | 'pinch' | null = null, start: Record<string, number> = null!,
    tap: { id: number; x: number; y: number; t: number } | null = null, lastTap: { t: number; x: number; y: number } | null = null;
  function rect() { return stage.getBoundingClientRect(); }
  function beginOne(p: Point) {
    const cat = catByKey[S.selKey], st = C.state[S.selKey];
    if (S.moveMode && canMove(cat, st) && inBox(p.x, p.y)) {
      mode = 'part';
      start = { x: p.x, y: p.y, tx: Number(st.tx) || 0, ty: Number(st.ty) || 0, k: SIZE / S.stageSvg.getBoundingClientRect().width };
      stage.classList.add('dragging');
    } else if (S.view.s > 1.001) {
      mode = 'pan';
      start = { x: p.x, y: p.y, vx: S.view.x, vy: S.view.y, w: rect().width };
      stage.classList.add('panning');
      beginGesture();
    } else mode = null;
  }
  function beginPinch() {
    if (mode === 'part') { stage.classList.remove('dragging'); updateTools(); }
    stage.classList.remove('panning');
    const a = Array.from(pts.values()), r = rect();
    const mx = (a[0].x + a[1].x) / 2, my = (a[0].y + a[1].y) / 2;
    mode = 'pinch';
    beginGesture();
    // 指のまん中の下にあるキャラの点（割合）
    start = { d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) || 1, s: S.view.s, ux: S.view.x + (mx - r.left) / r.width / S.view.s, uy: S.view.y + (my - r.top) / r.height / S.view.s };
    tap = null;
  }
  stage.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    stage.setPointerCapture(e.pointerId);
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 2) { beginPinch(); return; }
    if (pts.size > 2) return;
    tap = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
    beginOne({ x: e.clientX, y: e.clientY });
  });
  stage.addEventListener('pointermove', (e) => {
    if (!pts.has(e.pointerId)) {
      if (e.pointerType === 'mouse') stage.style.cursor = S.moveMode && inBox(e.clientX, e.clientY) ? 'grab' : '';
      return;
    }
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (tap && tap.id === e.pointerId && Math.abs(e.clientX - tap.x) + Math.abs(e.clientY - tap.y) > 10) tap = null;
    if (mode === 'part') {
      const cat = catByKey[S.selKey];
      setOffset(cat, cat.IS_MOVE == 3 ? Math.round(start.tx + (e.clientX - start.x) * start.k) : null, Math.round(start.ty + (e.clientY - start.y) * start.k));
    } else if (mode === 'pan') {
      setViewTo({ s: S.view.s, x: start.vx - (e.clientX - start.x) / start.w / S.view.s, y: start.vy - (e.clientY - start.y) / start.w / S.view.s });
    } else if (mode === 'pinch' && pts.size >= 2) {
      const a = Array.from(pts.values()), r = rect();
      const mx = (a[0].x + a[1].x) / 2, my = (a[0].y + a[1].y) / 2;
      const s = Math.max(1, Math.min(VIEW_MAX, start.s * Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) / start.d));
      // 指のまん中の下にあった所が、指のまん中の下に来るように
      setViewTo({ s: s, x: start.ux - (mx - r.left) / r.width / s, y: start.uy - (my - r.top) / r.height / s });
    }
  });
  function end(e: PointerEvent) {
    if (!pts.has(e.pointerId)) return;
    pts.delete(e.pointerId);
    // タップ（動かさずに離した）を2回続けたら、拡大しているときは全身に
    if (e.type === 'pointerup' && tap && tap.id === e.pointerId && performance.now() - tap.t < 350) {
      const now = performance.now();
      if (lastTap && now - lastTap.t < 320 && Math.abs(lastTap.x - e.clientX) + Math.abs(lastTap.y - e.clientY) < 30 && S.view.s > 1.001) { lastTap = null; resetView(); }
      else {
        lastTap = { t: now, x: e.clientX, y: e.clientY };
        if (!(S.moveMode && inBox(e.clientX, e.clientY))) pickAt(e.clientX, e.clientY);
      }
    }
    if (mode === 'pinch') {
      if (pts.size === 1) {
        // 1本残ったら、そこから見る場所を動かす（移動中でもパーツは動かさない）
        const p = Array.from(pts.values())[0];
        mode = S.view.s > 1.001 ? 'pan' : null;
        start = { x: p.x, y: p.y, vx: S.view.x, vy: S.view.y, w: rect().width };
      } else if (!pts.size && S.view.s < 1.03) resetView(); // ほとんど全身まで戻したら、ぴったり全身に
    }
    if (!pts.size) {
      if (mode === 'part') updateTools();
      endGesture(0);
      stage.classList.remove('dragging', 'panning');
      mode = null;
      tap = null;
    }
  }
  stage.addEventListener('pointerup', end);
  stage.addEventListener('pointercancel', end);
  stage.addEventListener('wheel', (e) => {
    e.preventDefault();
    beginGesture();
    endGesture(220); // ホイールが止まって少ししたら描き直す
    const r = rect(), fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
    const s = Math.max(1, Math.min(VIEW_MAX, S.view.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015))));
    const ux = S.view.x + fx / S.view.s, uy = S.view.y + fy / S.view.s;
    if (s < 1.03 && e.deltaY > 0) { resetView(); return; }
    setViewTo({ s: s, x: ux - fx / s, y: uy - fy / s });
  }, { passive: false });
}
